import familyFieldDefinitions from "./family-fields.json";
import profileFieldDefinitions from "./profile-fields.json";
import { academicResourceBase } from "./academic-snapshots";

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const compactDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const numberFormatter = new Intl.NumberFormat();
const SWU_SOURCE_OFFSET = "+08:00";

export function parseTimestamp(value: string): Date | null {
  const explicitZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  const plain =
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?$/.exec(
      value,
    );
  const normalized =
    !explicitZone && plain
      ? `${plain[1]}-${plain[2]}-${plain[3]}T${plain[4]}:${plain[5]}:${plain[6] || "00"}${plain[7] || ""}${SWU_SOURCE_OFFSET}`
      : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(
  value: string | null | undefined,
  compact = false,
): string {
  if (!value) return "—";
  const date = parseTimestamp(value);
  if (!date) return "—";
  return (compact ? compactDateTimeFormatter : dateTimeFormatter).format(date);
}

export function formatMonthDayRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  const start = formatMonthDay(startDate);
  const end = formatMonthDay(endDate);
  return start && end ? `${start}至${end}` : "";
}

function formatMonthDay(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${month}月${day}日`;
}

export function formatBytes(value: number | null | undefined): string {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && size >= 1024; index += 1) {
    size /= 1024;
    unit = units[index];
  }
  return `${size >= 10 ? size.toFixed(1) : size.toFixed(2)} ${unit}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.floor(seconds))} 秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟`;
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)} 小时 ${Math.floor((seconds % 3600) / 60)} 分钟`;
  }
  return `${Math.floor(seconds / 86400)} 天 ${Math.floor((seconds % 86400) / 3600)} 小时`;
}

export function formatNumber(value: number | null | undefined): string {
  return numberFormatter.format(Number(value || 0));
}

export function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    "admin.login.failed": "登录失败",
    "admin.login.succeeded": "登录成功",
    "admin.logout": "退出登录",
    "user.data.refresh": "刷新用户数据",
    "user.session.revoke": "撤销单个用户会话",
    "user.sessions.revoke": "撤销用户会话",
    "user.delete": "删除用户",
    "snapshot.delete": "删除快照",
    "job.run": "触发定时任务",
    "publication.create": "发布公告或通知",
    "publication.delete": "软删除公告或通知",
    "publication.restore": "恢复公告或通知",
    "publication.media.upload": "上传公告图片",
    "auto-dorm-check.feature.update": "调整自动查寝全局设置",
  };
  return labels[action] || action;
}

export function resourceLabel(resource: string): string {
  if (resource.startsWith("graduate:")) {
    return `研究生 · ${resourceLabel(academicResourceBase(resource))}`;
  }
  if (resource === "grades") return "成绩";
  if (resource === "schedule") return "本地日程同步副本";
  if (resource === "messages") return "教务消息预览";
  if (resource === "notices") return "学校通知预览";
  if (resource === "electricity") return "寝室绑定与电费";
  if (resource === "exams:catalog") return "考试可用学期";
  if (resource.startsWith("exams:")) {
    return `考试安排 · ${resource.slice("exams:".length)}`;
  }
  if (resource.startsWith("timetable:")) {
    const semester = resource.slice("timetable:".length);
    return semester === "default" ? "当前课表" : `课表 · ${semester}`;
  }
  return resource;
}

export function profileEntries(
  profile: Record<string, unknown>,
): Array<{ key: string; label: string; value: string }> {
  type Definition = {
    source: string;
    key: string;
    label: string;
    codeKey?: string;
    aliases?: string[];
    legacyKeys?: string[];
    legacyCodeKeys?: string[];
    hidden?: boolean;
    type?: string;
  };
  const definitions: Definition[] = profileFieldDefinitions;
  const labels: Record<string, string> = {};
  for (const field of definitions) {
    for (const key of [
      field.key,
      field.source,
      ...(field.aliases || []),
      ...(field.legacyKeys || []),
    ])
      labels[key.toUpperCase()] = field.label;
  }
  const firstValue = (keys: string[]) =>
    keys.map((key) => formatFamilyValue(profile[key], labels)).find(Boolean) ||
    "";
  const firstCode = (keys: string[]) =>
    keys.map((key) => profileCodeText(profile[key])).find(Boolean) || "";
  const entries: Array<{ key: string; label: string; value: string }> = [];
  for (const field of definitions) {
    if (field.hidden) continue;
    const sources = [field.source, ...(field.aliases || [])];
    let value = firstValue([
      field.key,
      ...(field.legacyKeys || []),
      ...sources.map((key) => key + "_DISPLAY"),
    ]);
    if (field.codeKey) {
      const code = firstCode([
        field.codeKey,
        ...(field.legacyCodeKeys || []),
        field.source,
      ]);
      if (!value) value = firstValue(field.aliases || []);
      if (!value) {
        const option = firstValue([field.source]);
        if (option !== code) value = option;
      }
      const unresolved = /^\d+(?:[,、]\d+)*$/.test(value);
      if (unresolved) value = "";
      if (!value && /[\u4e00-\u9fff]/.test(code)) value = code;
      if (code && value === code && !/[\u4e00-\u9fff]/.test(value)) value = "";
      if (!value && (code || unresolved)) value = "未获取";
    } else if (!value) value = firstValue(sources);
    if (field.type === "date") value = formatFamilyDate(value);
    if (field.type === "month") value = formatProfileMonth(value);
    if (value) entries.push({ key: field.key, label: field.label, value });
  }
  return entries;
}

function profileCodeText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const text = value.trim();
    if (/^[\[{]/.test(text)) {
      try {
        return profileCodeText(JSON.parse(text));
      } catch {
        /* Keep plain codes. */
      }
    }
    return text;
  }
  if (Array.isArray(value))
    return value.map(profileCodeText).filter(Boolean).join(",");
  if (typeof value === "object") {
    const option = value as Record<string, unknown>;
    for (const key of ["id", "code", "value", "DM"]) {
      const code = profileCodeText(option[key]);
      if (code) return code;
    }
    return "";
  }
  return String(value);
}

function formatProfileMonth(value: string): string {
  const match = /^(\d{4})[-/]?(\d{2})(?:$|[-/ T])/.exec(value);
  if (!match || Number(match[2]) < 1 || Number(match[2]) > 12) return value;
  return `${match[1]}年${Number(match[2])}月`;
}

export function familyMembers(
  profile: Record<string, unknown>,
): Array<Record<string, unknown>> {
  return Array.isArray(profile.familyMembers)
    ? profile.familyMembers.filter(
        (member): member is Record<string, unknown> =>
          Boolean(member) &&
          typeof member === "object" &&
          !Array.isArray(member),
      )
    : [];
}

export function familyMemberEntries(
  member: Record<string, unknown>,
): Array<{ key: string; label: string; value: string }> {
  type Field = {
    key: string;
    canonicalKey: string;
    label?: string;
    hidden?: boolean;
    display?: boolean;
  };
  const lookup = new Map<string, Field>();
  const labels: Record<string, string> = {};
  for (const field of familyFieldDefinitions) {
    const displayKey = field.displayKey || field.key + "Display";
    lookup.set(field.source.toUpperCase(), {
      ...field,
      canonicalKey: field.key,
    });
    lookup.set(field.key.toUpperCase(), { ...field, canonicalKey: field.key });
    for (const key of field.legacyKeys || []) {
      lookup.set(key.toUpperCase(), { ...field, canonicalKey: field.key });
    }
    lookup.set((field.source + "_DISPLAY").toUpperCase(), {
      ...field,
      display: true,
      canonicalKey: displayKey,
    });
    lookup.set(displayKey.toUpperCase(), {
      ...field,
      display: true,
      canonicalKey: displayKey,
    });
    if (field.label) {
      for (const key of [field.source, field.key, displayKey])
        labels[key.toUpperCase()] = field.label;
    }
  }
  const fields = new Map<
    string,
    {
      field: Field;
      raw?: unknown;
      display?: unknown;
      rawCanonical?: boolean;
      displayCanonical?: boolean;
    }
  >();
  for (const [key, value] of Object.entries(member)) {
    const note = /^(?:BZ|notes)(\d+)(?:_?DISPLAY)?$/i.exec(key);
    const noteKey = note?.[1] === "1" ? "notes" : "notes" + note?.[1];
    const display = /DISPLAY$/i.test(key);
    const field =
      lookup.get(key.toUpperCase()) ||
      (note
        ? {
            key: noteKey,
            canonicalKey: noteKey + (display ? "Display" : ""),
            label: "备注" + (note[1] === "1" ? "" : note[1]),
            display,
          }
        : { key, canonicalKey: key, label: key });
    if (field.hidden) continue;
    const entry = fields.get(field.key) || { field };
    const canonical = key.toUpperCase() === field.canonicalKey.toUpperCase();
    if (field.display) {
      if (canonical || !entry.displayCanonical) {
        entry.display = value;
        entry.displayCanonical = canonical;
      }
    } else if (canonical || !entry.rawCanonical) {
      entry.raw = value;
      entry.rawCanonical = canonical;
    }
    fields.set(field.key, entry);
  }
  const order = familyFieldDefinitions.map((field) => field.key);
  const fieldRank = (key: string) => {
    const index = order.indexOf(key);
    if (index >= 0) return index;
    return /^notes\d+$/.test(key) ? order.indexOf("notes6") + 1 : order.length;
  };
  return [...fields.values()]
    .sort((left, right) => {
      const a = fieldRank(left.field.key),
        b = fieldRank(right.field.key);
      return (
        a - b ||
        left.field.key.localeCompare(right.field.key, undefined, {
          numeric: true,
        })
      );
    })
    .map((entry) => {
      const value =
        formatFamilyValue(entry.display, labels) ||
        formatFamilyValue(entry.raw, labels);
      return {
        key: entry.field.key,
        label: entry.field.label || entry.field.key,
        value:
          entry.field.key === "dateOfBirth" ? formatFamilyDate(value) : value,
      };
    })
    .filter((entry) => entry.value.trim() !== "");
}

function formatFamilyValue(
  value: unknown,
  labels: Record<string, string>,
): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "string") {
    const text = value.trim();
    if (/^[\[{]/.test(text)) {
      try {
        return formatFamilyValue(JSON.parse(text), labels);
      } catch {
        /* Keep non-JSON text intact. */
      }
    }
    return text;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatFamilyValue(item, labels))
      .filter(Boolean);
    return parts.join(
      parts.some((part) => part.includes("\n")) ? "\n\n" : "、",
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value);
    // Select controls can return option objects instead of display strings.
    const optionKeys = new Set([
      "id",
      "value",
      "code",
      "label",
      "text",
      "name",
      "display",
      "caption",
      "title",
      "DM",
      "MC",
    ]);
    if (entries.every(([key]) => optionKeys.has(key))) {
      const option = value as Record<string, unknown>;
      for (const key of [
        "display",
        "label",
        "text",
        "name",
        "caption",
        "title",
        "MC",
      ]) {
        const display = formatFamilyValue(option[key], labels);
        if (display) return display;
      }
    }
    return entries
      .map(([key, item]) => {
        const text = formatFamilyValue(item, labels);
        return text ? `${labels[key.toUpperCase()] || key}：${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return String(value);
}

function formatFamilyDate(value: string): string {
  const match = /^(\d{4})[-/]?(\d{2})[-/]?(\d{2})(?:$|[ T])/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== `${year}-${month}-${day}`
  )
    return value;
  return `${year}-${month}-${day}`;
}
